import { useRef, useState } from 'react';
import { Input as RawInput, InputProps } from './Input';
import { debounce, identity, pipe } from '../../utils/fn';

type FloatProps = Omit<InputProps, 'type' | 'pattern' | 'onChange'> & {
    onChange: (event: React.ChangeEvent<{ value: number }>) => void;
};

const floatRegex = /^-?\d+(\.\d*)?$/;

export function Float(props: FloatProps) {
    const [internal, setInternal] = useState(String(props.value ? parseFloat(String(props.value)) || '' : ''));

    const debouncedUpdateRef = useRef(
        debounce((event) => {
            const minFilter: Filter = props.min ? createMinOrEmptyFilter(Number(props.min)) : identity;

            const normalizeValue = pipe(toFloatOrEmpty, minFilter);
            const theNormalizedValue = normalizeValue(event.target.value);

            setInternal(theNormalizedValue);

            if (theNormalizedValue !== '') {
                props?.onChange({
                    ...event,
                    target: {
                        ...event.target,
                        value: Number(theNormalizedValue),
                    },
                    currentTarget: {
                        ...event.currentTarget,
                        value: parseFloat(theNormalizedValue),
                    },
                });
            }
        }),
    );

    return (
        <RawInput
            type='text'
            inputMode='decimal'
            pattern={floatRegex.source}
            {...props}
            value={internal}
            size={internal.length || 1}
            onChange={(event) => {
                setInternal(event.target.value);
                debouncedUpdateRef.current(event);
            }}
        />
    );
}

type Filter = (value: string) => string;

const normalizeFloatString: Filter = (value) => `${parseFloat(value) || ''}`;
const toFloatOrEmpty: Filter = (value) => (floatRegex.test(value) ? value : normalizeFloatString(value));
const createMinOrEmptyFilter: (min: number) => Filter = (min) => (value) => {
    const valueAsNumber = parseFloat(value) || -Infinity;

    return valueAsNumber >= min ? value : String(min);
};
